import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { EnrollmentScreenLayout } from '@/presentation/layouts/EnrollmentScreenLayout';
import { CardReader } from '@/presentation/components/CardReader';
import { useCardReader } from '@/modules/attendance/presentation/hooks/useCardReader';
import { runEnrollCardSaveGate } from '@/modules/admin/application/enrollCardSaveGate';
import { useEnrollCardMutation } from '@/modules/admin/application/enrollMutations';
import { db } from '@/shared/db/database';
import type { EmployeeDTO } from '@/modules/employees/infrastructure/dto/EmployeeDTO';
import { enrollBiometricLocally } from '@/modules/admin/application/use-cases/enrollBiometricLocallyUseCase';
import { isNetworkOrTimeoutError } from '@/shared/api/classifyApiError';
import { getLocalEmployeeRepository } from '@/modules/attendance/infrastructure/repositories/container';
import { TEMPLATE_CARD } from '@/modules/employees/domain/biometricConstants';

// ── Types ─────────────────────────────────────────────────────────────────────

type Phase = 'idle' | 'reading' | 'done' | 'error';

const IDLE_TO_READING_MS = 600;

// ── Component ─────────────────────────────────────────────────────────────────

export function AdminEnrollCardPage() {
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const { idPerson } = useParams<{ idPerson: string }>();
  const { t } = useTranslation('common');

  const [phase, setPhase] = useState<Phase>('idle');
  const [alreadyEnrolledMsg, setAlreadyEnrolledMsg] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const processingRef = useRef(false);

  // ── Auto-transition idle → reading after 600ms ────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setPhase('reading');
    }, IDLE_TO_READING_MS);
    return () => { clearTimeout(timer); };
  }, []);

  const { mutateAsync: enrollCard } = useEnrollCardMutation();

  // ── Card handler ──────────────────────────────────────────────────────────
  const handleCard = useCallback(async (rawInput: string) => {
    if (phase !== 'reading' || processingRef.current) return;
    processingRef.current = true;
    setAlreadyEnrolledMsg(false);
    setErrorMsg('');

    const decimal = parseInt(rawInput, 10);
    if (isNaN(decimal)) {
      processingRef.current = false;
      return;
    }

    // Hoisted so catch can access it for network-error offline fallback
    let resolvedIdEntity: number | undefined;

    try {
      // 1. Fetch employee from local DB (need both biometric_data and id_entity)
      const row = await db.employees.where('idPerson').equals(Number(idPerson)).first();
      const dto = row?.raw as EmployeeDTO | undefined;
      const biometricData = dto?.biometric_data ?? [];
      resolvedIdEntity = dto?.id_entity;

      // Guard: idEntity is required by the enrollment endpoint
      if (!resolvedIdEntity) {
        setErrorMsg(t('admin.enrollCard.errorGeneric'));
        setPhase('error');
        return;
      }

      // 2. Run local duplicate gate
      const gate = runEnrollCardSaveGate({ decimal, biometricData });
      if (gate.conflict) {
        setAlreadyEnrolledMsg(true);
        return;
      }

      // 3. Offline path — skip HTTP, save locally + enqueue for later sync
      if (!navigator.onLine) {
        await enrollBiometricLocally({
          idPerson: Number(idPerson),
          idEntity: resolvedIdEntity,
          biometric: { templateType: 'card', decimal },
        });
        setPhase('done');
        setTimeout(() => { navigate(-1); }, 1500);
        return;
      }

      // 4. Online path — call API, then patch local DB for immediate offline use
      await enrollCard({ idEntity: resolvedIdEntity, decimal });
      await getLocalEmployeeRepository().patchBiometricData(Number(idPerson), {
        nro_biometric: 1,
        biometric_data: {
          decimal:       String(decimal),
          card_number:   decimal,
          decimal_short: decimal,
          format_card:   '',
          facility_code: 0,
        },
        id_template_biometric: TEMPLATE_CARD,
        metadata:              [],
        id_entity:             resolvedIdEntity,
        at_created:            new Date().toISOString(),
      });
      setPhase('done');
      setTimeout(() => { navigate(-1); }, 1500);

    } catch (err: unknown) {
      // Network timeout → treat as offline, save locally + enqueue
      if (isNetworkOrTimeoutError(err) && resolvedIdEntity) {
        await enrollBiometricLocally({
          idPerson: Number(idPerson),
          idEntity: resolvedIdEntity,
          biometric: { templateType: 'card', decimal },
        });
        setPhase('done');
        setTimeout(() => { navigate(-1); }, 1500);
        return;
      }

      // Duplicate card error from API
      const message = (err as Error)?.message ?? '';
      const isDuplicate =
        message.toLowerCase().includes('duplicate') ||
        message.toLowerCase().includes('already') ||
        message.toLowerCase().includes('already enrolled');

      if (isDuplicate) {
        navigate(`/admin/empleados/${idPerson}/enroll/card/duplicate`, {
          state: { conflictingEmployee: { fullName: null } },
        });
        return;
      }

      setErrorMsg(t('admin.enrollCard.errorGeneric'));
      setPhase('error');
    } finally {
      processingRef.current = false;
    }
  }, [phase, idPerson, enrollCard, navigate, t]);

  const handleCardSync = useCallback((rawInput: string) => {
    void handleCard(rawInput);
  }, [handleCard]);

  const { inputRef, handleBlur, handleChange, handleKeyDown } = useCardReader(handleCardSync);

  // ── Retry ─────────────────────────────────────────────────────────────────
  const handleRetry = useCallback(() => {
    setPhase('reading');
    setErrorMsg('');
    setAlreadyEnrolledMsg(false);
    processingRef.current = false;
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <EnrollmentScreenLayout
      onBack={() => { navigate(-1); }}
      title={t('admin.enrollCard.title')}
      badgeLabel={t('admin.enrollBadge')}
      badgeColor={C.blue}
      T={T}
    >
      {/* Card reader visual */}
      <div style={{ marginBottom: 36 }}>
        <CardReader
          scanning={phase === 'reading'}
          animationClass="mi-card-pulse-ring"
          T={T}
        />
      </div>

      {/* State display */}
      {phase === 'done' ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 46, height: 46, borderRadius: '50%', background: C.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.success }}>{t('admin.enrollCard.success')}</div>
        </div>

      ) : phase === 'error' ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 12, color: C.danger, fontWeight: 600, textAlign: 'center', maxWidth: 280 }}>{errorMsg}</div>
          <button
            data-testid="retry-button"
            onClick={handleRetry}
            style={{ padding: '8px 22px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
          >
            {t('admin.enrollCard.retry')}
          </button>
        </div>

      ) : phase === 'reading' ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{t('admin.enrollCard.scanning')}</div>
          {alreadyEnrolledMsg && (
            <div
              data-testid="already-enrolled-msg"
              style={{ fontSize: 11, color: C.danger, fontWeight: 600, textAlign: 'center', maxWidth: 280, padding: '6px 14px', borderRadius: 8, background: `${C.danger}12` }}
            >
              {t('admin.enrollCard.alreadyEnrolled')}
            </div>
          )}
        </div>

      ) : (
        /* idle */
        <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600 }}>{t('admin.enrollCard.scanning')}</div>
      )}

      {/* Hidden HID input — aria-label (not aria-hidden) so focus is allowed */}
      <input
        ref={inputRef}
        data-testid="hid-input"
        type="text"
        aria-label="Card reader input"
        tabIndex={-1}
        autoComplete="off"
        style={{ position: 'fixed', opacity: 0, width: 1, height: 1, top: 0, left: 0, pointerEvents: 'none' }}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      {/* Test-only triggers — never visible in production (display:none) */}
      <button
        data-testid="__test-force-reading__"
        style={{ display: 'none' }}
        onClick={() => { setPhase('reading'); }}
      />
      <button
        data-testid="__test-card-submit__"
        style={{ display: 'none' }}
        onClick={(e) => {
          const val = (e.currentTarget as HTMLButtonElement).dataset['value'] ?? '';
          void handleCard(val);
        }}
      />

    </EnrollmentScreenLayout>
  );
}
